import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loading } from '../components/Loading';
import { AuthLayout } from '../components/auth/AuthLayout';
import { getButtonClasses } from '@/components/ui/atoms/Button';
import { signupIntent } from '../services/auth.service';

interface InvitationData {
  email: string;
  tenant_name?: string | null;
  tenant_id?: string | null;
  assigned_plan_code?: string | null;
  status: string;
  user_exists?: boolean;
}

const apiUrl = import.meta.env.VITE_API_SERVER_URL as string;

/**
 * Custom invitation landing — replaces the old bounce-through-Auth0 flow.
 * Validates the token, persists the invitation as the post-auth intent,
 * then routes to /signup or /login depending on whether the email already
 * has an Invirtus account.
 */
export const InvitationLandingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing invitation token');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          `${apiUrl}/api/invitations/validate/${encodeURIComponent(token)}`,
        );
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          if (!cancelled) {
            setError(
              data.detail ||
                'This invitation is invalid or has expired. Ask the inviter to send a fresh one.',
            );
          }
          return;
        }
        const data: InvitationData = await resp.json();
        if (cancelled) return;

        if (data.status === 'expired') {
          setError(
            'This invitation has expired. Ask the inviter to send a new one.',
          );
          return;
        }

        signupIntent.set({
          plan: data.assigned_plan_code || 'invitation',
          invitation_token: token,
        });

        const target = data.user_exists ? '/login' : '/signup';
        const params = new URLSearchParams({ invitation: token });
        if (data.email) params.set('email', data.email);
        navigate(`${target}?${params.toString()}`, { replace: true });
      } catch {
        if (!cancelled) setError('Could not validate invitation');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  if (error) {
    return (
      <AuthLayout
        planTitle={t('invitation_landing.plan_title')}
        planTagline={t('invitation_landing.plan_tagline')}
        description={t('invitation_landing.plan_description')}
        planFeatures={[
          { text: t('invitation_landing.feature_1') },
          { text: t('invitation_landing.feature_2') },
          { text: t('invitation_landing.feature_3') },
        ]}
      >
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-dark-invirtus">
            {t('invitation_landing.error_title')}
          </h1>
          <p className="text-sm text-dark-invirtus/70">{error}</p>
          <Link
            to="/login"
            className={getButtonClasses(
              'contained',
              'secondary',
              'md',
              false,
              'w-full justify-center no-underline',
            )}
          >
            {t('invitation_landing.go_to_sign_in')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return <Loading />;
};

export default InvitationLandingPage;
